import { useState, useEffect } from 'react';

interface QuickLocation {
  name: string;
  coordinates: [number, number];
}

interface UseNearbyPlacesParams {
  currentView: string;
  startLocation: QuickLocation | null;
  selectedDayLocation: QuickLocation | null;
  userId: string | undefined;
  maxDistance: number;
  selectedTypes: string[];
  showOnlySaved: boolean;
  apiBaseUrl: string;
}

/**
 * Custom hook to fetch and manage nearby places based on location and filters
 * 
 * Fetches places from the backend API when location or filters change,
 * handles loading state, and manages request cancellation via AbortController.
 * 
 * @param params - Configuration parameters for nearby places fetching
 * @returns Nearby places data, loading state, and setter function
 */
export function useNearbyPlaces(params: UseNearbyPlacesParams) {
  const {
    currentView,
    startLocation,
    selectedDayLocation,
    userId,
    maxDistance,
    selectedTypes,
    showOnlySaved,
    apiBaseUrl,
  } = params;

  const [nearbyPlaces, setNearbyPlaces] = useState<any[]>([]);
  const [placesLoading, setPlacesLoading] = useState(false);

  // Fetch nearby places when view changes or filters change
  useEffect(() => {
    // Determine which location to use based on current view
    let locationToUse: QuickLocation | null = null;
    if (currentView === 'day-trip-destinations' && startLocation) {
      locationToUse = startLocation;
    } else if (currentView === 'multi-day-trip-day-destinations' && selectedDayLocation) {
      locationToUse = selectedDayLocation;
    }

    if (!locationToUse) {
      return;
    }

    // If savedOnly filter is on but user is not authenticated, skip fetch
    if (showOnlySaved && !userId) {
      setNearbyPlaces([]);
      setPlacesLoading(false);
      return;
    }

    // Create AbortController to cancel previous requests
    const abortController = new AbortController();

    const fetchNearbyPlaces = async () => {
      setPlacesLoading(true);
      try {
        const requestBody = {
          userId: userId || null, // Optional for unauthenticated users
          latitude: locationToUse!.coordinates[1], // lat
          longitude: locationToUse!.coordinates[0], // lng
          maxDistance: maxDistance,
          types: selectedTypes,
          savedOnly: showOnlySaved,
        };
        
        const response = await fetch(`${apiBaseUrl}/api/advanced-trip/nearby-places`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(requestBody),
          signal: abortController.signal, // Add abort signal
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch nearby places: ${response.statusText}`);
        }

        const data = await response.json();
        setNearbyPlaces(data.places || []);
        setPlacesLoading(false);
      } catch (error: any) {
        // Ignore abort errors
        if (error.name === 'AbortError') {
          return;
        }
        setNearbyPlaces([]);
        setPlacesLoading(false);
      }
    };

    fetchNearbyPlaces();

    // Cleanup function to abort request when dependencies change
    return () => {
      abortController.abort();
    };
  }, [currentView, startLocation, selectedDayLocation, userId, maxDistance, selectedTypes, showOnlySaved, apiBaseUrl]);

  return {
    nearbyPlaces,
    setNearbyPlaces,
    placesLoading,
    setPlacesLoading,
  };
}

